import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { X, Search, ChevronLeft } from "lucide-react";
import { AGE_GROUPS, setAgeGroup, getAgeSeen, setAgeSeen } from "@/lib/ageGroup";
import { COUNTRIES, flagUrl, continentById } from "@/data/countries";
import { stampCountry } from "@/lib/progression";
import { loadProgress, saveProgress } from "@/lib/progress";
import {
  getBornInSeen,
  setBornInSeen,
  getKidName,
  setKidName,
  getBirthCountryCode,
  setBirthCountryCode,
  ensureFirstVisitDay,
} from "@/lib/kidProfile";

/**
 * First-visit onboarding — a SINGLE two-step modal:
 *   step 1 · "age"     : pick tier (Seedlings / Explorers / Pioneers)
 *   step 2 · "profile" : optional name + birth country
 *
 * This replaces the previous flow where AgeSelector closed and a second
 * BornInPicker popup opened moments later (double-popup complaint).
 * BornInPicker still exists for the menu's "Change my country" pill so
 * parents can edit heritage later — but it no longer auto-opens.
 *
 * Consumers can still re-open the age picker imperatively via
 *   window.dispatchEvent(new Event('we-open-age-selector'))
 * (used by the "Change age" chip). Re-opening skips straight to step 1
 * and never advances to step 2 unless the child re-picks an age.
 */
export default function AgeSelector() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("age"); // "age" | "profile"
  const [pendingRoute, setPendingRoute] = useState(null);

  // Profile-step state
  const [name, setName] = useState("");
  const [selectedCode, setSelectedCode] = useState("");
  const [query, setQuery] = useState("");

  const navigate = useNavigate();

  // First-visit behaviour changed (Feb 2026): do NOT auto-open the
  // 4-step setup on the very first load — analytics showed engagement
  // dropped to 53s because visitors saw a modal wall before ever
  // interacting with the app. Instead we let them explore the spinning
  // globe / Country of the Day, and the Home page renders a friendly
  // "Set up my journey" / "Quick look" bar (see FirstVisitCTA) that
  // triggers this modal only if they opt in. AgeSelector still opens
  // automatically when a child hits an age-gated feature (Practice,
  // TreasureQuest, etc.) via the `we-open-age-selector` event.
  //
  // Kept intentionally: nothing. The `getAgeSeen()` guard now protects
  // both auto-open and the Day-1 streak seed.

  // Imperative re-open. Always jumps to step 1 (age).
  useEffect(() => {
    const onOpen = () => {
      setStep("age");
      setPendingRoute(null);
      setOpen(true);
    };
    window.addEventListener("we-open-age-selector", onOpen);
    return () => window.removeEventListener("we-open-age-selector", onOpen);
  }, []);

  // Prefill profile fields when advancing to step 2 (so returning parents
  // see previous name / country instead of a blank form).
  useEffect(() => {
    if (step === "profile") {
      setName(getKidName());
      setSelectedCode(getBirthCountryCode());
      setQuery("");
    }
  }, [step]);

  const chooseAge = (g) => {
    const isFirstTime = !getAgeSeen();
    setAgeGroup(g.id);
    setAgeSeen();
    // FLOW-01 — First Day Welcome: seed the streak on the very first
    // age pick so the child sees Day 1 as part of onboarding, not as
    // a feature they stumble into later. We only do this once — the
    // guard is `!getAgeSeen()` before the setAgeSeen() call above.
    if (isFirstTime) {
      try {
        const p = loadProgress();
        if (!p.lastPlayed) {
          p.streak = 1;
          p.lastPlayed = new Date().toISOString().slice(0, 10);
          saveProgress(p);
          setTimeout(() => {
            toast.success("Day 1 streak started! 🔥", { description: "Come back tomorrow to grow it." });
          }, 800);
        }
      } catch { /* streak is a nice-to-have, never block onboarding */ }
    }
    // Remember where the tier wants to route to, but wait until profile
    // step is done before actually navigating.
    if (g.recommendStart && g.recommendStart !== window.location.pathname) {
      setPendingRoute(g.recommendStart);
    } else {
      setPendingRoute(null);
    }
    // If parents have already completed the profile once, don't re-ask.
    if (getBornInSeen()) {
      closeAll();
    } else {
      setStep("profile");
    }
  };

  const closeAll = () => {
    // Skipping leaves the view neutral (all ages) — age band is a preference, never required.
    setAgeSeen();
    setOpen(false);
    if (pendingRoute) {
      navigate(pendingRoute);
      setPendingRoute(null);
    }
  };

  // Step-1 X button: skip everything (both age & profile). Mark both as seen.
  const skipAge = () => {
    setBornInSeen();
    ensureFirstVisitDay();
    closeAll();
  };

  // Step-2 Skip: age is saved, but the child didn't want to share a name/
  // country. Mark the born-in step as seen so it never nags again.
  const skipProfile = () => {
    setBornInSeen();
    ensureFirstVisitDay();
    closeAll();
  };

  const backToAge = () => setStep("age");

  const submitProfile = () => {
    const cleanName = name.trim();
    if (cleanName) setKidName(cleanName);
    if (selectedCode) {
      setBirthCountryCode(selectedCode);
      const country = COUNTRIES.find((c) => c.code === selectedCode);
      if (country) {
        stampCountry(country.code, {
          fact: country.funFact,
          continent: country.continent,
          home: true,
        });
      }
      const nice = cleanName || "explorer";
      const cName = country ? country.name : "Your country";
      toast.success(`Welcome, ${nice}! ${cName} is your first stamp 🌟`);
    } else if (cleanName) {
      toast(`Hi ${cleanName}! Let's explore the world 🌍`, { icon: "👋" });
    }
    setBornInSeen();
    ensureFirstVisitDay();
    try {
      window.dispatchEvent(new Event("we-personalize-applied"));
    } catch { /* ignore */ }
    closeAll();
  };

  // Country search (limited to 60 results so the grid paints fast on
  // low-end tablets).
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return COUNTRIES.slice(0, 60);
    return COUNTRIES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q) ||
        (c.capital || "").toLowerCase().includes(q),
    ).slice(0, 60);
  }, [query]);

  const selectedCountry = useMemo(
    () => COUNTRIES.find((c) => c.code === selectedCode) || null,
    [selectedCode],
  );

  const cardRef = useRef(null);

  const isAge = step === "age";
  const canSubmit = !!name.trim() || !!selectedCode;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={isAge ? skipAge : skipProfile}
          className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-3 sm:p-4"
          data-testid="age-selector-overlay"
        >
          <motion.div
            ref={cardRef}
            onClick={(e) => e.stopPropagation()}
            initial={{ scale: 0.85, y: 30 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.85, opacity: 0 }}
            transition={{ type: "spring", damping: 22, stiffness: 240 }}
            role="dialog"
            aria-modal="true"
            aria-label={isAge ? "Choose your age" : "Tell us about you"}
            data-testid="age-selector"
            className="relative w-full max-w-2xl rounded-3xl border-4 border-slate-900 bg-white shadow-[10px_10px_0_0_rgba(15,23,42,1)] flex flex-col max-h-[94vh] sm:max-h-[90vh] overflow-hidden"
          >
            <button
              type="button"
              onClick={isAge ? skipAge : skipProfile}
              data-testid="age-selector-close"
              aria-label={isAge ? "Skip" : "Skip"}
              className="absolute top-2.5 right-2.5 z-10 h-9 w-9 inline-flex items-center justify-center rounded-full border-[3px] border-slate-900 bg-white shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all"
            >
              <X size={18} strokeWidth={3} />
            </button>

            {/* Compact step indicator so parents know it's a 2-step flow. */}
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5" data-testid="age-selector-steps">
              <span className={`h-2 w-6 rounded-full border-2 border-slate-900 ${isAge ? "bg-emerald-400" : "bg-emerald-200"}`} />
              <span className={`h-2 w-6 rounded-full border-2 border-slate-900 ${!isAge ? "bg-emerald-400" : "bg-slate-100"}`} />
            </div>

            <AnimatePresence mode="wait">
              {isAge ? (
                <motion.div
                  key="step-age"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.18 }}
                  className="overflow-y-auto px-4 pt-9 pb-4 sm:px-6 sm:pt-10 sm:pb-6 md:px-8 md:pb-8"
                >
                  <div className="text-center mb-4 sm:mb-6">
                    <img
                      src="/brand-mark-full.webp"
                      alt="My World Quest — Explore, Play, Learn, Grow"
                      data-testid="age-selector-brand"
                      className="mx-auto h-14 sm:h-24 md:h-32 w-auto object-contain mb-2 sm:mb-3"
                      loading="eager"
                    />
                    <div className="text-3xl sm:text-4xl md:text-5xl mb-1" aria-hidden>🌱🌿🌳</div>
                    <h2 className="font-heading text-xl sm:text-3xl md:text-4xl font-bold leading-tight">
                      Make MWQ fit you 👋
                    </h2>
                    <p className="mt-1.5 text-sm sm:text-base md:text-lg text-slate-700 font-bold">
                      Choose an age group to make activities, navigation and recommendations more relevant.
                    </p>
                  </div>

                  <div className="grid grid-cols-3 gap-2 sm:gap-4">
                    {AGE_GROUPS.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        onClick={() => chooseAge(g)}
                        data-testid={`age-pick-${g.id}`}
                        aria-label={`${g.label} — ages ${g.ageRange}`}
                        className={`group rounded-2xl sm:rounded-3xl border-[3px] sm:border-4 border-slate-900 ${g.color} p-3 sm:p-6 text-center shadow-[4px_4px_0_0_rgba(15,23,42,1)] sm:shadow-[6px_6px_0_0_rgba(15,23,42,1)] hover:-translate-x-[2px] hover:-translate-y-[2px] hover:shadow-[6px_6px_0_0_rgba(15,23,42,1)] sm:hover:shadow-[8px_8px_0_0_rgba(15,23,42,1)] transition-all`}
                      >
                        <div className="text-4xl sm:text-6xl md:text-7xl mb-1 sm:mb-2" aria-hidden>{g.emoji}</div>
                        <div className="font-heading text-lg sm:text-2xl md:text-3xl font-bold leading-tight">
                          {g.band}
                        </div>
                        <div className="text-[10px] sm:text-sm md:text-base font-bold text-slate-700 mt-1">
                          {g.label}
                        </div>
                      </button>
                    ))}
                  </div>

                  <p className="mt-3 sm:mt-5 text-center text-[11px] sm:text-xs font-bold text-slate-500">
                    Not sure? You can change this anytime from More — or skip and see everything.
                  </p>

                  {/* One-line parental-consent acknowledgement written to
                      universal privacy-first standards for children.
                      Continuing past the age picker constitutes explicit
                      consent to save progress on this device. Parents
                      can view/withdraw/export/delete from /parents. */}
                  <p
                    className="mt-2 text-center text-[10px] font-bold text-slate-500 leading-snug px-2"
                    data-testid="age-consent-line"
                  >
                    By continuing, a parent/caregiver confirms they consent to saving progress on this device only.{" "}
                    <a
                      href="/privacy"
                      className="underline text-emerald-700"
                      onClick={(e) => e.stopPropagation()}
                      target="_self"
                      rel="noopener"
                    >
                      Read the privacy notice
                    </a>{" "}
                    ·{" "}
                    <a
                      href="/parents"
                      className="underline text-slate-600"
                      onClick={(e) => e.stopPropagation()}
                      target="_self"
                      rel="noopener"
                    >
                      Withdraw or delete anytime
                    </a>
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="step-profile"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.18 }}
                  className="flex flex-col min-h-0 flex-1"
                >
                  {/* Sticky header */}
                  <div className="bg-yellow-200 border-b-4 border-slate-900 px-4 pt-9 pb-3 sm:px-6 flex-shrink-0">
                    <button
                      type="button"
                      onClick={backToAge}
                      data-testid="profile-back"
                      className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-bold text-slate-700 hover:text-slate-900 mb-1"
                    >
                      <ChevronLeft size={14} strokeWidth={3} /> Back
                    </button>
                    <h2 className="font-heading text-xl sm:text-2xl font-bold leading-tight">
                      Make it personal 🌟
                    </h2>
                    <p className="mt-0.5 text-xs sm:text-sm font-bold text-slate-700 leading-snug">
                      Add your name and home country — we&apos;ll start your passport
                      with your country stamp!
                    </p>
                  </div>

                  {/* Scrollable body */}
                  <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 sm:px-6 sm:py-4">
                    {/* Name field */}
                    <label className="block">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        Child&apos;s first name
                      </span>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="born-in-name"
                        placeholder="e.g. Aisha"
                        maxLength={40}
                        autoComplete="off"
                        className="mt-1 w-full rounded-xl border-[3px] border-slate-900 bg-white px-3 py-2 font-bold text-sm sm:text-base focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/60 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                      />
                    </label>

                    {/* Country picker */}
                    <div className="mt-3">
                      <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                        Country of birth
                      </span>

                      {selectedCountry && (
                        <div
                          className="mt-1 flex items-center gap-2 rounded-xl border-[3px] border-slate-900 bg-emerald-100 p-2"
                          data-testid="born-in-selected"
                        >
                          <img
                            src={flagUrl(selectedCountry.code)}
                            alt={`${selectedCountry.name} flag`}
                            width={48}
                            height={30}
                            className="h-7 w-11 object-cover rounded-md border-2 border-slate-900"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-sm truncate">
                              {selectedCountry.name}
                            </p>
                            <p className="text-[11px] font-bold text-slate-600 truncate">
                              {continentById(selectedCountry.continent)?.name}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedCode("")}
                            data-testid="born-in-clear"
                            className="text-[11px] font-bold underline text-slate-600 hover:text-slate-900"
                          >
                            Change
                          </button>
                        </div>
                      )}

                      {!selectedCountry && (
                        <div className="mt-1 relative">
                          <Search
                            size={14}
                            strokeWidth={3}
                            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
                          />
                          <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="born-in-search"
                            placeholder="Type to search countries (optional)…"
                            className="w-full rounded-xl border-[3px] border-slate-900 bg-white pl-8 pr-3 py-2 font-bold text-sm focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-amber-500/60 shadow-[0_3px_0_0_rgba(15,23,42,1)]"
                          />
                        </div>
                      )}

                      {/* Country grid — hidden until the parent starts typing,
                          so the modal opens quiet and uncluttered. */}
                      {!selectedCountry && query.trim() && (
                        <div
                          className="mt-2 grid grid-cols-4 sm:grid-cols-6 gap-1.5"
                          data-testid="born-in-grid"
                        >
                          {filtered.map((c) => (
                            <button
                              key={c.code}
                              type="button"
                              onClick={() => setSelectedCode(c.code)}
                              data-testid={`born-in-country-${c.code}`}
                              className="group rounded-lg border-2 border-slate-900 bg-white p-0.5 shadow-[0_2px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_1px_0_0_rgba(15,23,42,1)] transition-all text-left"
                              aria-label={`Pick ${c.name}`}
                            >
                              <img
                                src={flagUrl(c.code)}
                                alt=""
                                width={64}
                                height={38}
                                className="w-full aspect-[5/3] object-cover rounded border border-slate-900"
                                loading="lazy"
                              />
                              <p className="mt-0.5 text-[9px] font-bold text-center truncate leading-tight">
                                {c.name}
                              </p>
                            </button>
                          ))}
                          {!filtered.length && (
                            <p className="col-span-full text-center text-[11px] font-bold text-slate-500 py-3">
                              No countries match &ldquo;{query}&rdquo;
                            </p>
                          )}
                        </div>
                      )}

                      {!selectedCountry && !query.trim() && (
                        <p className="mt-1.5 text-[11px] font-bold text-slate-500 text-center">
                          Optional — leave blank to skip.
                        </p>
                      )}
                    </div>

                    <p className="mt-2 text-center text-[10px] font-bold text-slate-500">
                      Saved on this device only. You can edit anytime from the menu.
                    </p>
                  </div>

                  {/* Sticky footer */}
                  <div className="border-t-4 border-slate-900 bg-slate-50 px-3 py-2.5 flex items-center gap-2 flex-shrink-0">
                    <button
                      type="button"
                      onClick={skipProfile}
                      data-testid="born-in-skip-btn"
                      className="px-3 py-2 rounded-xl border-[3px] border-slate-900 bg-white font-bold text-xs sm:text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all whitespace-nowrap"
                    >
                      Skip
                    </button>
                    <button
                      type="button"
                      onClick={submitProfile}
                      disabled={!canSubmit}
                      data-testid="born-in-submit"
                      className="flex-1 px-3 py-2 rounded-xl border-[3px] border-slate-900 bg-emerald-300 font-bold text-sm sm:text-base shadow-[0_4px_0_0_rgba(15,23,42,1)] hover:translate-y-[1px] hover:shadow-[0_3px_0_0_rgba(15,23,42,1)] active:translate-y-[2px] active:shadow-[0_2px_0_0_rgba(15,23,42,1)] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:hover:shadow-[0_4px_0_0_rgba(15,23,42,1)] whitespace-nowrap"
                    >
                      Start exploring 🌍
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
